import React, { FC, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@src/svg';
import { toast } from 'react-toastify';
import { useDisableScroll } from '@hooks/useDisableScroll';
import ConfirmModal from '@src/modals/ConfirmModal/ConfirmModal';
import { isFormDirty } from '@utils/Form/form-monitor';
import Draggable from 'react-draggable';

type ModalProps = {
  title?: string;
  titleIcon?: ReactNode;
  children: ReactNode;
  maxWidth?: string;
  className?: string;
  disableDrag?: boolean;
  needConfirm?: boolean;
  closeClick: () => void;
  wrapperClassName?: string;
  closeOutside?: boolean;
  dataTestId?: string;
  closeDisabled?: boolean;
};

const Modal: FC<ModalProps> = ({
  title = '',
  titleIcon,
  children,
  maxWidth,
  closeClick,
  disableDrag = false,
  needConfirm = true,
  className,
  wrapperClassName,
  closeOutside,
  dataTestId,
  closeDisabled = false,
}) => {
  useDisableScroll();

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const handleClose = () => {
    if (closeDisabled) return;

    const hasUnsavedChanges = isFormDirty();

    if (hasUnsavedChanges && needConfirm) {
      setOpenConfirm(true);
    } else {
      setOpenConfirm(false);
      closeClick();
    }
  };

  const handleAdjustClick = (e) => {
    closeOutside && e.stopPropagation();
  };

  const handleCloseOutside = () => {
    closeOutside && handleClose();
  };

  const closeConfirm = () => {
    setOpenConfirm(false);
    closeClick();
  };

  const modalContent = (
    <div
      className={`modal__container ${className}`}
      style={{ maxWidth: maxWidth || '1018px' }}
      onClick={handleAdjustClick}
    >
      <div
        className="modal__header"
        style={disableDrag ? undefined : { cursor: 'move' }}
      >
        {titleIcon ? (
          <div className="modal__title-with-icon">
            <span className="modal__title-icon">{titleIcon}</span>
            <h2>{title}</h2>
          </div>
        ) : (
          <h2>{title}</h2>
        )}
        <button
          className="close-btn"
          data-test-id={`${dataTestId}-close-btn`}
          disabled={closeDisabled}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toast.dismiss();
            handleClose();
          }}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="modal__body" data-test-id={`${dataTestId}-modal-body`}>
        {children}
      </div>
    </div>
  );

  return createPortal(
    <div
      className={`modal ${wrapperClassName ?? ''}`}
      onClick={handleCloseOutside}
    >
      {disableDrag ? (
        modalContent
      ) : (
        <Draggable
          handle=".modal__header"
          cancel=".modal__body"
          bounds="parent"
        >
          {modalContent}
        </Draggable>
      )}

      {openConfirm && (
        <ConfirmModal
          onConfirm={closeConfirm}
          onCancel={() => setOpenConfirm(false)}
          message="Are you sure?"
        />
      )}
    </div>,
    document.body
  );
};

export default Modal;
